import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaBook,
  FaClipboardList,
  FaBookOpen,
  FaCheckCircle,
} from "react-icons/fa";
import academicStyles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import styles from "./Assessmentcreate.module.css";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import {
  CustomButton,
  CustomTextInput,
  CustomDropdown,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";

const DIFFICULTY_OPTIONS = [
  { value: "Easy", label: "Easy" },
  { value: "Medium", label: "Medium" },
  { value: "Hard", label: "Hard" },
];

const MCQ_OPTION_OPTIONS = [
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
];

const PASS_PERCENTAGE_OPTIONS = [
  { value: 20, label: "20%" },
  { value: 25, label: "25%" },
  { value: 30, label: "30%" },
  { value: 35, label: "35%" },
  { value: 40, label: "40%" },
  { value: 45, label: "45%" },
  { value: 50, label: "50%" },
  { value: 55, label: "55%" },
  { value: 60, label: "60%" },
  { value: 65, label: "65%" },
  { value: 70, label: "70%" },
  { value: 75, label: "75%" },
  { value: 80, label: "80%" },
];

const MAX_QUESTIONS = 10;
const MAX_REGENERATE = 3;

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];

function AssessmentCreate() {
  const navigate = useNavigate();
  const { t, translations } = useSelector((state) => state.language || {});
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const client_id = clientData?.client_id || "";
  const master_id = clientData?.master_id || "";
  const staff_id = master_id || "";
  const user_id = user?.id || "";
  const academic_id = clientData?.academic_year_id || "";
  // ==================== STATE ====================
  const [formData, setFormData] = useState({
    book_id: "",
    bookName: "",
    class_id: "",
    className: "",
    subject_id: "",
    subject: "",
    medium: "",
    section: "",
    sectionOptions: [],
    topic: "",
    sub_topic: "",
    timeDuration: "",
    start_page: "",
    end_page: "",
    min_page: "",
    max_page: "",
    num_questions: "",
    difficulty_level: "",
    mcq_options: "",
    pass_percentage: 50,
  });
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Dropdown data sources
  const [bookList, setBookList] = useState([]);
  const [topicList, setTopicList] = useState([]); // array of { topic, sub_topics }
  const [subTopicList, setSubTopicList] = useState([]);

  // Loading flags
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Generated questions & regenerate counter
  const [questions, setQuestions] = useState([]);
  const [regenerateCount, setRegenerateCount] = useState(0);
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);

  // PDF viewer state — comes from getStaffBook (info.filePath)
  const [pdfUrl, setPdfUrl] = useState("");

  /* Blob-aware pdfUrl loader:
     - Azure blob paths (dev/<clientId>/Upload/Books/x.pdf — no protocol,
       no leading "/") are fetched through the authenticated
       /content/image endpoint and shown via an object URL
     - legacy paths keep the old IMAGE_BASE_URL static URL */
  const applyPdfUrl = async (rawFilePath) => {
    const bookFilePath = rawFilePath || "";
    if (!bookFilePath) {
      setPdfUrl("");
      return;
    }
    const isBlobPath =
      !/^https?:\/\//i.test(bookFilePath) &&
      !bookFilePath.startsWith("/") &&
      bookFilePath.includes("/Upload/");
    if (isBlobPath) {
      try {
        const res = await AxiosInstance.get("/content/image", {
          params: { path: bookFilePath },
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" }),
        );
        setPdfUrl(objectUrl);
      } catch (err) {
        console.error("Error loading book PDF from blob:", err);
        setPdfUrl("");
      }
      return;
    }
    const cleanFilePath = bookFilePath.startsWith("/")
      ? bookFilePath.slice(1)
      : bookFilePath;
    setPdfUrl(
      cleanFilePath.startsWith("http")
        ? cleanFilePath
        : `${IMAGE_BASE_URL || ""}${cleanFilePath}`,
    );
  };

  // Modal state — `redirectPath` is set ONLY for popups that should
  // navigate the user away on OK (e.g. after a successful Submit).
  // Generate-success popups leave it empty so OK simply closes.
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // ==================== OPTIONS (memoized) ====================
  const bookOptions = useMemo(
    () =>
      bookList.map((b) => {
        const info = b.info || {};
        return {
          value: info.id,
          label: b.book_name,
          raw: b,
        };
      }),
    [bookList],
  );

  const topicOptions = useMemo(
    () =>
      topicList.map((tp) => {
        if (typeof tp === "string") {
          return { value: tp, label: tp };
        }
        const v = tp.topic || tp.name || "";
        return { value: v, label: v };
      }),
    [topicList],
  );

  const subTopicOptions = useMemo(
    () =>
      subTopicList.map((st) => {
        if (typeof st === "string") {
          return { value: st, label: st };
        }
        const v = st.sub_topic || st.name || "";
        return { value: v, label: v };
      }),
    [subTopicList],
  );

  // ==================== API: Load Books ====================
  useEffect(() => {
    if (school_id && staff_id) {
      fetchStaffBooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, staff_id]);

  const fetchStaffBooks = async () => {
    setLoadingBooks(true);
    try {
      const response = await AxiosInstance.post(`/book/getStaffBook`, {
        school_id: school_id,
        staff_id: staff_id,
      });
      if (response?.data?.status) {
        setBookList(response.data.data || []);
      } else {
        setBookList([]);
      }
    } catch (error) {
      console.error("Error fetching staff books:", error);
      setBookList([]);
    } finally {
      setLoadingBooks(false);
    }
  };

  // ==================== API: Load Topics / Sub-Topics ====================
  const fetchBookTopics = async (bookId, topic = "", subTopic = "") => {
    if (!bookId) return;
    setLoadingTopics(true);
    try {
      const payload = {
        school_id: school_id,
        id: bookId,
      };
      if (topic) payload.topic = topic;
      if (subTopic) payload.sub_topic = subTopic;

      const response = await AxiosInstance.post(`/book/getBookTopic`, payload);

      if (response?.data?.status) {
        const data = response.data.data;

        if (!topic && !subTopic) {
          // CASE 1: Only book_id passed — response.data is an ARRAY of { topic, sub_topics }
          setTopicList(Array.isArray(data) ? data : []);
          setSubTopicList([]);
        } else {
          // CASE 2 & 3: topic OR topic+sub_topic passed — data is an OBJECT
          const obj = data || {};
          const minPage =
            obj.pdf_start_page ?? obj.start_page ?? obj.min_page ?? "";
          const maxPage =
            obj.pdf_end_page ?? obj.end_page ?? obj.max_page ?? "";

          if (topic && !subTopic) {
            // Set sub-topics from this response
            setSubTopicList(obj.sub_topics || []);
          }

          setFormData((prev) => ({
            ...prev,
            min_page: minPage,
            max_page: maxPage,
            start_page: minPage,
            end_page: maxPage,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching book topics:", error);
    } finally {
      setLoadingTopics(false);
    }
  };

  // ==================== HANDLERS ====================
  const handleDropdownChange = (name, selectedOption) => {
    // ✅ Block any change after a successful generate (form is locked)
    if (hasGeneratedOnce) return;

    const value = selectedOption?.value ?? "";
    const label = selectedOption?.label ?? "";

    if (name === "book_id") {
      const bookRecord = bookList.find((b) => b?.info?.id === value);
      const info = bookRecord?.info || {};

      const sectionArr = Array.isArray(info.section_id)
        ? info.section_id
        : info.section_id
          ? [info.section_id]
          : [];
      const sectionOpts = sectionArr.map((s) => ({ value: s, label: s }));

      // ✅ Get PDF URL from getStaffBook response (info.filePath)
      // Blob-aware: legacy static URL or Azure blob object URL
      const rawFilePath = info.filePath || "";

      setFormData((prev) => ({
        ...prev,
        book_id: value,
        bookName: label,
        class_id: info.class_id || "",
        className: info.class || "",
        subject_id: info.subject_id || "",
        subject: info.subject || "",
        medium: info.medium || "",
        sectionOptions: sectionOpts,
        section: sectionOpts.length === 1 ? sectionOpts[0].value : "",
        topic: "",
        sub_topic: "",
        start_page: "",
        end_page: "",
        min_page: "",
        max_page: "",
      }));

      applyPdfUrl(rawFilePath);
      setTopicList([]);
      setSubTopicList([]);
      setQuestions([]);
      setRegenerateCount(0);
      setHasGeneratedOnce(false);
      setErrors((prev) => ({
        ...prev,
        book_id: "",
        section: "",
      }));

      if (value) fetchBookTopics(value);
    } else if (name === "section") {
      setFormData((prev) => ({ ...prev, section: value }));
      setErrors((prev) => ({ ...prev, section: "" }));
    } else if (name === "topic") {
      const topicObj = topicList.find(
        (tp) => (typeof tp === "string" ? tp : tp?.topic) === value,
      );
      const subTopicsFromTopic =
        topicObj &&
        typeof topicObj === "object" &&
        Array.isArray(topicObj.sub_topics)
          ? topicObj.sub_topics
          : [];

      setFormData((prev) => ({
        ...prev,
        topic: value,
        sub_topic: "",
        start_page: "",
        end_page: "",
      }));
      setSubTopicList(subTopicsFromTopic);
      setErrors((prev) => ({ ...prev, topic: "" }));

      if (value && formData.book_id) {
        fetchBookTopics(formData.book_id, value);
      }
    } else if (name === "sub_topic") {
      setFormData((prev) => ({ ...prev, sub_topic: value }));
      setErrors((prev) => ({ ...prev, sub_topic: "" }));
      if (value && formData.book_id && formData.topic) {
        fetchBookTopics(formData.book_id, formData.topic, value);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputChange = (e) => {
    // ✅ Block any change after a successful generate (form is locked)
    if (hasGeneratedOnce) return;

    const { name, value } = e.target;

    // Number of Question — only digits, max 2 chars, max value 10, no negatives
    if (name === "num_questions") {
      if (value === "") {
        setFormData((prev) => ({ ...prev, num_questions: "" }));
        setErrors((prev) => ({ ...prev, num_questions: "" }));
        return;
      }

      // Only digits allowed (no minus, no decimal, no letters)
      if (!/^\d+$/.test(value)) return;

      // 🚫 Block leading zero — rejects "0", "00", "01", "05", etc.
      if (value.startsWith("0")) return;

      // Max 2 digits
      if (value.length > 2) return;

      const n = Number(value);

      // Max value 10 — block anything larger
      if (n > MAX_QUESTIONS) return;

      setFormData((prev) => ({ ...prev, num_questions: value }));
      setErrors((prev) => ({ ...prev, num_questions: "" }));
      return;
    }

    // Page fields — only digits, max 4 digits
    const pageFields = ["start_page", "end_page"];
    if (pageFields.includes(name)) {
      if (value !== "" && !/^\d{0,4}$/.test(value)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ==================== VALIDATION ====================
  const validateGenerate = () => {
    const newErrors = {};
    if (!formData.book_id)
      newErrors.book_id = t?.bookRequired || "Book is required";
    if (!formData.section)
      newErrors.section = t?.sectionRequired || "Section is required";
    if (!formData.topic)
      newErrors.topic = t?.topicRequired || "Topic is required";
    if (!formData.start_page)
      newErrors.start_page = t?.startPageRequired || "Start Page is required";
    if (!formData.timeDuration)
      newErrors.timeDuration =
        t?.timeDurationRequired || "Time Duraion is required";
    if (!formData.end_page)
      newErrors.end_page = t?.endPageRequired || "End Page is required";
    if (!formData.num_questions)
      newErrors.num_questions =
        t?.numQuestionsRequired || "Number of Questions is required";
    else if (/^0/.test(formData.num_questions))
      newErrors.num_questions =
        t?.numQuestionsNoLeadingZero || "Number cannot start with 0";
    else if (Number(formData.num_questions) > MAX_QUESTIONS)
      newErrors.num_questions =
        t?.maxQuestionsAllowed || `Maximum ${MAX_QUESTIONS} questions allowed`;
    else if (Number(formData.num_questions) < 1)
      newErrors.num_questions =
        t?.minQuestionRequired || "Minimum 1 question required";
    if (!formData.difficulty_level)
      newErrors.difficulty_level =
        t?.difficultyLevelRequired || "Difficulty Level is required";
    if (!formData.mcq_options)
      newErrors.mcq_options =
        t?.mcqOptionsRequired || "MCQ Options Count is required";
    if (!formData.pass_percentage)
      newErrors.pass_percentage =
        t?.passPercentageRequired || "Pass Percentage is required";

    const sp = Number(formData.start_page);
    const ep = Number(formData.end_page);
    const minP = Number(formData.min_page);
    const maxP = Number(formData.max_page);
    if (sp && ep && sp > ep) {
      newErrors.end_page =
        t?.endPageGreaterStart || "End Page must be >= Start Page";
    }
    if (minP && sp && sp < minP) {
      newErrors.start_page = `${t?.startPageMin || "Start Page must be >="} ${minP}`;
    }
    if (maxP && ep && ep > maxP) {
      newErrors.end_page = `${t?.endPageMax || "End Page must be <="} ${maxP}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== API: Generate Assessment ====================
  const handleGenerate = async () => {
    if (!validateGenerate()) return;
    if (regenerateCount >= MAX_REGENERATE) return;

    setGenerating(true);
    setGenerateSuccess(false);

    try {
      const payload = {
        school_id,
        book_id: formData.book_id,
        topic: formData.topic,
        sub_topic: formData.sub_topic,
        num_questions: Number(formData.num_questions),
        difficulty_level: formData.difficulty_level,
        mcq_options: Number(formData.mcq_options),
        pass_percentage: Number(formData.pass_percentage),
        start_page: Number(formData.start_page),
        end_page: Number(formData.end_page),
      };

      const response = await AxiosInstance.post(
        `/book/assessment/generate`,
        payload,
      );

      const respData = response?.data || {};

      if (respData.success === true || respData.status === true) {
        const generatedQuestions = respData.questions || [];
        setQuestions(generatedQuestions);
        setRegenerateCount((c) => c + 1);
        setHasGeneratedOnce(true);
        setGenerateSuccess(true);

        // 👉 Generate success: NO redirect on OK — user stays on the
        // page to review questions and click Submit.
        setModalState({
          open: true,
          type: "success",
          message:
            respData.message ||
            t?.assessmentGeneratedSuccess ||
            "Assessment generated successfully",
          redirectPath: "",
        });
      } else {
        setGenerateSuccess(false);
        setModalState({
          open: true,
          type: "Failed",
          message:
            respData.message ||
            t?.failedToGenerateAssessment ||
            "Failed to generate assessment",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error generating assessment:", error);
      setGenerateSuccess(false);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message ||
          t?.failedToGenerateAssessment ||
          "Failed to generate assessment",
        redirectPath: "",
      });
    } finally {
      setGenerating(false);
    }
  };

  // ==================== API: Submit Assessment ====================
  const handleSubmit = async () => {
    if (questions.length === 0) return;

    setSubmitting(true);
    try {
      const payload = {
        client_id: client_id,
        school_id: school_id,
        user_id: user_id,
        academic_id: academic_id,
        book_id: formData.book_id,
        class: formData.class_id,
        section: formData.section,
        medium: formData.medium,
        subject: formData.subject,
        topic: formData.topic,
        sub_topic: formData.sub_topic,
        pass_percentage: Number(formData.pass_percentage),
        start_page: Number(formData.start_page),
        end_page: Number(formData.end_page),
        duration: Number(formData.timeDuration),
        difficulty_level: formData.difficulty_level,
        mcq_options: Number(formData.mcq_options),
        num_questions: Number(formData.num_questions),
        questions: questions.map((q, idx) => ({
          question_no: q.question_no ?? idx + 1,
          question: q.question,
          correct_option: q.correct_option,
          explanation: q.explanation,
          option_a: q.option_a,
          option_b: q.option_b,
          option_c: q.option_c,
          option_d: q.option_d,
          ...(q.option_e ? { option_e: q.option_e } : {}),
        })),
      };

      const response = await AxiosInstance.post(
        `/book/assessment/store`,
        payload,
      );

      if (response?.data?.status || response?.data?.success) {
        // 👉 Submit success: redirect to assessment list when user
        // clicks OK on the popup.
        setModalState({
          open: true,
          type: "success",
          message:
            response.data.message ||
            t?.assessmentSavedSuccess ||
            "Assessment saved successfully",
          redirectPath: "/user/assessmentList",
        });
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            response?.data?.message ||
            t?.failedToSaveAssessment ||
            "Failed to save assessment",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting assessment:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message ||
          t?.failedToSaveAssessment ||
          "Failed to save assessment",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleModalClose = () => {
    setModalState({ open: false, type: "", message: "", redirectPath: "" });
  };

  // ==================== SUCCESS OK HANDLER ====================
  // Closes the modal, then navigates to `redirectPath` if one is set.
  // Only the Submit-success popup sets a redirectPath, so the
  // Generate-success popup just closes and stays on this page.
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  // ==================== DERIVED FLAGS ====================
  const showPreviewSection = hasGeneratedOnce;
  const regenerateLimitReached = regenerateCount >= MAX_REGENERATE;
  const inputsLocked = hasGeneratedOnce; // ✅ All inputs disabled after generate succeeds

  // ==================== RENDER ====================
  return (
    <div>
      {/* ========== CARD 1: Book Details + Generate Assessment ========== */}
      <div className={academicStyles.container}>
        {/* Header — Book Details */}
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaBook />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.bookDetails || "Book Details"}
          </h1>
        </div>

        {/* ROW 1 — Book / Class / Section / Medium */}
        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.book || "Book"}
              name="book_id"
              value={formData.book_id}
              onChange={(value) => handleDropdownChange("book_id", value)}
              options={bookOptions}
              placeholder={
                loadingBooks
                  ? t?.loading || "Loading..."
                  : t?.selectBook || "Select Book"
              }
              required
              disabled={inputsLocked}
              error={errors.book_id}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.class || "Class"}
              name="className"
              value={formData.className}
              placeholder={t?.selectClass || "Class"}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.section || "Section"}
              name="section"
              value={formData.section}
              onChange={(value) => handleDropdownChange("section", value)}
              options={formData.sectionOptions}
              placeholder={
                !formData.book_id
                  ? t?.selectBookFirst || "Select Book First"
                  : t?.selectSection || "Select Section"
              }
              disabled={!formData.book_id || inputsLocked}
              required
              error={errors.section}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.medium || "Medium"}
              name="medium"
              value={formData.medium}
              placeholder={t?.selectMedium || "Medium"}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* ROW 2 — Topic / Sub Topic / Start Page / End Page */}
        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.topic || "Topic"}
              name="topic"
              value={formData.topic}
              onChange={(value) => handleDropdownChange("topic", value)}
              options={topicOptions}
              placeholder={
                !formData.book_id
                  ? t?.selectBookFirst || "Select Book First"
                  : loadingTopics
                    ? t?.loading || "Loading..."
                    : t?.selectTopicPlaceholder || "Select Topic"
              }
              required
              disabled={!formData.book_id || inputsLocked}
              error={errors.topic}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.subTopic || "Sub Topic"}
              name="sub_topic"
              value={formData.sub_topic}
              onChange={(value) => handleDropdownChange("sub_topic", value)}
              options={subTopicOptions}
              placeholder={
                !formData.topic
                  ? t?.selectTopicFirst || "Select Topic First"
                  : t?.selectSubTopic || "Select Sub Topic"
              }
              disabled={!formData.topic || inputsLocked}
              error={errors.sub_topic}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.startPageLabel || "Start Page"}
              name="start_page"
              value={formData.start_page}
              onChange={handleInputChange}
              required
              disabled={inputsLocked}
              readOnly={inputsLocked}
              placeholder={t?.enterStartPage || "Enter Start Page"}
              error={errors.start_page}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.endPageLabel || "End Page"}
              name="end_page"
              value={formData.end_page}
              onChange={handleInputChange}
              required
              disabled={inputsLocked}
              readOnly={inputsLocked}
              placeholder={t?.enterEndPage || "Enter End Page"}
              error={errors.end_page}
            />
          </div>
        </div>

        {/* ========== Generate Assessment Section ========== */}
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.generateAssessment || "Generate Assessment"}
          </h1>
        </div>

        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.numberOfQuestion || "Number of Question"}
              name="num_questions"
              type="text"
              maxLength={2}
              inputMode="numeric"
              value={formData.num_questions}
              onChange={handleInputChange}
              required
              disabled={inputsLocked}
              readOnly={inputsLocked}
              placeholder={
                t?.enterNumberOfQuestions || "Enter Number of Questions"
              }
              error={errors.num_questions}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.difficultyLevel || "Difficulty Level"}
              name="difficulty_level"
              value={formData.difficulty_level}
              onChange={(value) =>
                handleDropdownChange("difficulty_level", value)
              }
              options={DIFFICULTY_OPTIONS}
              placeholder={t?.selectDifficulty || "Select Difficulty"}
              required
              disabled={inputsLocked}
              error={errors.difficulty_level}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.mcqOptionsCount || "MCQ Options Count"}
              name="mcq_options"
              value={formData.mcq_options}
              onChange={(value) => handleDropdownChange("mcq_options", value)}
              options={MCQ_OPTION_OPTIONS}
              placeholder={t?.enterOptionsCount || "Enter Options Count"}
              required
              disabled={inputsLocked}
              error={errors.mcq_options}
            />
          </div>

          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.passPercentage || "Pass Percentage"}
              name="pass_percentage"
              value={formData.pass_percentage}
              onChange={(value) =>
                handleDropdownChange("pass_percentage", value)
              }
              options={PASS_PERCENTAGE_OPTIONS}
              placeholder={t?.selectPassPercentage || "Select"}
              required
              disabled={inputsLocked}
              error={errors.pass_percentage}
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.timeDurationMins || "Time Duration (mins)"}
              name="timeDuration"
              type="text" // text + inputMode lets us fully control input
              inputMode="numeric"
              maxLength={2} // max value 30 is always 2 digits
              value={formData.timeDuration}
              onChange={(e) => {
                const raw = e.target.value;

                // Allow empty (user clearing the field)
                if (raw === "") {
                  handleInputChange(e);
                  return;
                }

                // Only digits, max 2 chars
                if (!/^\d{1,2}$/.test(raw)) return;

                const num = Number(raw);

                // 1-digit: 1–9 all valid (could still grow into 10–30)
                // Block "0" alone — leading zero
                if (raw.length === 1) {
                  if (num < 1) return;
                }

                // 2-digit: must be 10–30
                if (raw.length === 2) {
                  if (num < 10 || num > 30) return;
                }

                handleInputChange(e);
              }}
              onKeyDown={(e) => {
                // Block 'e', 'E', '+', '-', '.', ',' if they ever sneak through
                if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              required
              placeholder={
                t?.enterTimeDuration || "Enter time duration (1-30m)"
              }
              error={errors.timeDuration}
            />
          </div>
        </div>

        {/* ==========================================================
            BUTTON ROW INSIDE CARD 1
            - Before first generate → CANCEL + GENERATE
            - After first generate → only RE-GENERATE (no cancel here)
            - After 3 re-generates → button disabled
            ========================================================== */}
        <div className={styles.buttonRowCenter}>
          {!hasGeneratedOnce && (
            <CustomButton
              type="button"
              variant="contained"
              className={academicStyles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {t?.cancel || "CANCEL"}
            </CustomButton>
          )}

          <CustomButton
            type="button"
            variant="contained"
            className={academicStyles.addButton}
            fullWidth={false}
            onClick={handleGenerate}
            disabled={generating || regenerateLimitReached}
          >
            {generating
              ? t?.generating || "Generating..."
              : !hasGeneratedOnce
                ? t?.generateAssessment || "Generate Assessment"
                : `${t?.regenerate || "Re-Generate"} ${regenerateCount}/${MAX_REGENERATE}`}
          </CustomButton>
        </div>
      </div>

      {/* ========================================================
          PDF + PREVIEW + 2nd CANCEL/SUBMIT ROW
          Visible ONLY after the first successful Generate.
          ======================================================== */}
      {showPreviewSection && (
        <>
          <div className={`row mt-3 ${styles.previewRow}`}>
            {/* Book PDF */}
            <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
              <div className={styles.panelCard}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelHeaderIcon}>
                    <FaBookOpen />
                  </div>
                  <h1 className={styles.panelHeaderTitle}>
                    {t?.bookPdfTitle || "Book PDF"}
                  </h1>
                </div>
                <div className={styles.pdfBody}>
                  <PdfViewer
                    file={pdfUrl}
                    fromPage={Number(formData.start_page) || undefined}
                    toPage={Number(formData.end_page) || undefined}
                    fallbackMessage={
                      t?.pdfFallbackMessage || "PDF will appear here"
                    }
                    enableFullscreen
                  />
                </div>
              </div>
            </div>

            {/* Preview Assessment */}
            <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
              <div className={styles.panelCard}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelHeaderIcon}>
                    <FaClipboardList />
                  </div>
                  <h1 className={styles.panelHeaderTitle}>
                    {t?.previewAssessment || "Preview Assessment"}
                  </h1>
                </div>

                <div className={styles.previewBody}>
                  {questions.length === 0 ? (
                    <div className={styles.emptyState}>
                      {t?.noQuestionsGenerated || "No questions generated yet"}
                    </div>
                  ) : (
                    questions.map((q, qIdx) => {
                      const optionCount = Number(formData.mcq_options) || 4;
                      const correct = (q.correct_option || "")
                        .toString()
                        .toLowerCase();

                      return (
                        <div key={qIdx} className={styles.questionBlock}>
                          <div className={styles.questionText}>
                            {qIdx + 1}. {q.question}
                          </div>

                          <div className={styles.optionsGrid}>
                            {OPTION_LETTERS.slice(0, optionCount).map(
                              (letter) => {
                                const optKey = `option_${letter}`;
                                const optValue = q[optKey];
                                if (optValue === undefined || optValue === null)
                                  return null;

                                const isCorrect = correct === letter;

                                return (
                                  <div
                                    key={letter}
                                    className={styles.optionCol}
                                  >
                                    <div
                                      className={`${styles.optionCard} ${
                                        isCorrect
                                          ? styles.optionCardCorrect
                                          : ""
                                      }`}
                                    >
                                      <span className={styles.radioWrapper}>
                                        <input
                                          type="radio"
                                          name={`q-${qIdx}`}
                                          checked={isCorrect}
                                          readOnly
                                          className={styles.radioInput}
                                        />
                                        <span
                                          className={`${styles.radioMark} ${
                                            isCorrect
                                              ? styles.radioMarkChecked
                                              : ""
                                          }`}
                                        >
                                          {isCorrect && (
                                            <span
                                              className={styles.radioInner}
                                            />
                                          )}
                                        </span>
                                      </span>
                                      <span className={styles.optionText}>
                                        {letter}) {optValue}
                                      </span>
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>

                          {q.explanation && (
                            <div className={styles.explanationPanel}>
                              <FaCheckCircle
                                className={styles.explanationIcon}
                              />
                              <div className={styles.explanationContent}>
                                <div
                                  className={styles.explanationCorrectAnswer}
                                >
                                  {correct}) {q[`option_${correct}`] || ""}
                                </div>
                                <div className={styles.explanationText}>
                                  {t?.explanation || "Explanation"} :{" "}
                                  {q.explanation}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer — 2nd Cancel + Submit (visible only after generate) */}
          <div className={styles.buttonRowCenter}>
            <CustomButton
              type="button"
              variant="contained"
              className={academicStyles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {t?.cancel || "CANCEL"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={academicStyles.addButton}
              fullWidth={false}
              onClick={handleSubmit}
              disabled={
                submitting ||
                generating ||
                !generateSuccess ||
                questions.length === 0
              }
            >
              {submitting
                ? t?.submitting || "Submitting..."
                : t?.submit || "Submit"}
            </CustomButton>
          </div>
        </>
      )}

      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || t?.success || "Success"
            : modalState.type === "Confirm"
              ? translations?.labels?.delete || t?.delete || "DELETE"
              : translations?.labels?.failed || t?.failed || "Failed"
        }
        message={modalState.message}
        onClose={handleModalClose}
        onOk={handleSuccessOk}
      />
    </div>
  );
}

export default AssessmentCreate;
